import React from 'react';
import { Card, Group, Stack, Text, Skeleton, ThemeIcon } from '@mantine/core';
import {
  IconBuilding,
  IconCategory,
  IconPhone,
  IconWorld,
  IconMail,
  IconClock
} from '@tabler/icons-react';
import { BusinessMatchInfoProps } from './BusinessMatchInfo.types';

/**
 * BusinessMatchInfo Component
 *
 * Displays matched business details in a card format
 *
 * Features:
 * - Business name and category
 * - Contact information (phone, email, website)
 * - Verification status
 * - Opening hours
 * - Compact and detailed modes
 * - Click handling for navigation
 *
 * Usage:
 * ```tsx
 * <BusinessMatchInfo
 *   business={businessData}
 *   detailed
 *   onClick={handleBusinessClick}
 * />
 * ```
 */
export const BusinessMatchInfo: React.FC<BusinessMatchInfoProps> = ({
  business,
  loading = false,
  detailed = false,
  onClick,
  showVerification: _showVerification = true,
  compact = false,
  variant = 'outline'
}): React.ReactElement => {
  // Handle click
  const handleClick = (): void => {
    if (onClick && business) {
      onClick(business);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Card withBorder={variant === 'outline'} padding={compact ? 'sm' : 'md'}>
        <Stack gap="xs">
          <Skeleton height={20} width="70%" />
          <Skeleton height={16} width="50%" />
          {detailed && (
            <>
              <Skeleton height={14} width="80%" />
              <Skeleton height={14} width="60%" />
            </>
          )}
        </Stack>
      </Card>
    );
  }

  // No business data
  if (!business) {
    return (
      <Card withBorder={variant === 'outline'} padding={compact ? 'sm' : 'md'}>
        <Group gap="xs">
          <IconBuilding size={16} color="gray" />
          <Text size="sm" c="dimmed" fs="italic">
            No business match
          </Text>
        </Group>
      </Card>
    );
  }

  // Compact mode
  if (compact) {
    return (
      <Card withBorder={variant === 'outline'} padding="sm" onClick={handleClick}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="blue">
              <IconBuilding size={14} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text size="sm" fw={600} lineClamp={1}>
                {business.name}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {business.category}
              </Text>
            </Stack>
          </Group>
        </Group>
      </Card>
    );
  }

  // Detailed mode
  return (
    <Card
      withBorder={variant === 'outline'}
      padding={compact ? 'sm' : 'md'}
      shadow={variant === 'default' ? 'sm' : undefined}
      onClick={handleClick}
    >
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconBuilding size={20} />
            </ThemeIcon>
            <Stack gap={4}>
              <Text size="lg" fw={600} lineClamp={2}>
                {business.name}
              </Text>
              <Group gap="xs">
                <IconCategory size={14} />
                <Text size="sm" c="dimmed">
                  {business.category}
                </Text>
                {business.businessType && (
                  <>
                    <Text size="sm" c="dimmed">
                      •
                    </Text>
                    <Text size="sm" c="dimmed">
                      {business.businessType}
                    </Text>
                  </>
                )}
              </Group>
            </Stack>
          </Group>
        </Group>

        {/* Description */}
        {detailed && business.description && (
          <Text size="sm" c="dimmed" lineClamp={3}>
            {business.description}
          </Text>
        )}

        {/* Contact Information (detailed mode only) */}
        {detailed && (
          <Stack gap="xs">
            {business.phone && (
              <Group gap="xs">
                <IconPhone size={14} />
                <Text size="sm">{business.phone}</Text>
              </Group>
            )}

            {business.email && (
              <Group gap="xs">
                <IconMail size={14} />
                <Text size="sm">{business.email}</Text>
              </Group>
            )}

            {business.website && (
              <Group gap="xs">
                <IconWorld size={14} />
                <Text
                  size="sm"
                  c="blue"
                  component="a"
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {business.website}
                </Text>
              </Group>
            )}

            {business.openingHours && (
              <Group gap="xs">
                <IconClock size={14} />
                <Text size="sm">{business.openingHours}</Text>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
